import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import PortalLayout from '@/components/portal/PortalLayout';
// AdminRoute removed - AuthMiddleware handles all permission checking
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Terminal } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import ProviderSettingsForm from '@/components/admin/ProviderSettingsForm';
import {
  AIProviderClientEnum,
  ALL_PROVIDERS,
  GlobalAIProviderSetting,
  SetGlobalAIProviderSettingParams,
  SetGlobalAIProviderSettingResponse
} from '@/types/aiProviderTypes';

const fetchGlobalAISettings = async (): Promise<GlobalAIProviderSetting[]> => {
  const { data, error } = await supabase.rpc('get_global_ai_provider_settings');
  if (error) {
    console.error('Error fetching global AI settings:', error);
    // If the function doesn't exist, return empty array instead of throwing
    if (error.message?.includes('function') && error.message?.includes('does not exist')) {
      console.warn('Global AI provider settings function not available. Please apply the latest migrations.');
      return [];
    }
    throw new Error(error.message || 'Failed to fetch global AI settings.');
  }
  return data || [];
};

const GlobalAISettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: globalSettings,
    isLoading,
    isError,
    error
  } = useQuery<GlobalAIProviderSetting[], Error>({
    queryKey: ['globalAISettings'],
    queryFn: fetchGlobalAISettings,
  });

  // TODO: Implement mutation for saving settings for each provider
  // const setSettingMutation = useMutation(...);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mx-auto py-10">
        <Alert variant="destructive">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Error Fetching Settings</AlertTitle>
          <AlertDescription>
            {error?.message || 'An unknown error occurred.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Group settings by provider name for easier access
  const settingsMap = new Map<AIProviderClientEnum, GlobalAIProviderSetting>();
  globalSettings?.forEach(setting => settingsMap.set(setting.provider_name, setting));

  return (
    <PortalLayout>
      <div className="container mx-auto py-6 space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold">Global AI Provider Settings</h1>
          <p className="text-gray-600 mt-2">
            Configure global API keys, base URLs, and default models for AI providers.
            These settings will be used as fallbacks if specific chatbot AI configurations are not set.
          </p>
        </div>
        <Tabs defaultValue={ALL_PROVIDERS[0]} className="w-full">
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-4 lg:grid-cols-7 mb-4">
            {ALL_PROVIDERS.map((provider) => (
              <TabsTrigger key={provider} value={provider} className="capitalize">
                {provider}
              </TabsTrigger>
            ))}
          </TabsList>

            {ALL_PROVIDERS.map((provider) => {
              const currentSetting = settingsMap.get(provider);
              return (
                <TabsContent key={provider} value={provider}>
                  <Card>
                    <CardHeader>
                      <CardTitle className="capitalize">{provider} Settings</CardTitle>
                      {currentSetting && (
                        <AlertDescription className={currentSetting.has_api_key ? 'text-green-600' : 'text-orange-600'}>
                          API Key is {currentSetting.has_api_key ? 'SET' : 'NOT SET'}.
                          {currentSetting.is_active ? ' This provider is ACTIVE.' : ' This provider is INACTIVE.'}
                        </AlertDescription>
                      )}
                       {!currentSetting && (
                        <AlertDescription className='text-gray-500'>This provider has no global settings configured yet.</AlertDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-4 pt-2">
                      <ProviderSettingsForm provider={provider} currentSetting={currentSetting} />
                    </CardContent>
                  </Card>
                </TabsContent>
              );
            })}
        </Tabs>
      </div>
    </PortalLayout>
  );
};

export default GlobalAISettingsPage;
